import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, MapPin, Clock, Navigation, Send, CheckCircle2 } from "lucide-react";
import Reveal from "@/components/Reveal";
import {
  ADDRESS, HOURS, MAPS_URL, PHONE_DISPLAY, PHONE_TEL, SERVED_AREAS,
} from "@/constants/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROPERTY_TYPES = ["Residential", "Commercial", "Industrial", "Housing Society", "Other"];
const BILL_RANGES = ["Below ₹1,000", "₹1,000 – ₹2,500", "₹2,500 – ₹5,000", "₹5,000 – ₹10,000", "Above ₹10,000"];

const inputCls =
  "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-900 placeholder:text-slate-400 focus:border-orange-500 focus:outline-none focus:ring-4 focus:ring-orange-500/10 transition";

const QuoteForm = () => {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    city: "",
    property_type: "Residential",
    bill_range: "₹1,000 – ₹2,500",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, form);
      setDone(true);
      toast.success("Quote request received! We'll call you back within 24 hours.");
      setForm({ name: "", phone: "", city: "", property_type: "Residential", bill_range: "₹1,000 – ₹2,500", message: "" });
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = Array.isArray(detail) && detail[0]?.msg ? detail[0].msg : "Could not send your request. Please call us instead.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={onSubmit} data-testid="quote-form" className="rounded-3xl border border-slate-200 bg-white p-6 sm:p-8 shadow-sm">
      <h2 className="font-display text-2xl font-bold tracking-tight text-slate-950">Request a free quote</h2>
      <p className="mt-2 text-sm text-slate-500">Fill this in and we will call you back within 24 hours.</p>

      {done && (
        <div data-testid="quote-success" className="mt-5 flex items-start gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
          <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
          <p className="text-sm font-medium text-emerald-800">
            Thank you! Your request is in. Our team will call you within 24 hours — for anything urgent, call {PHONE_DISPLAY}.
          </p>
        </div>
      )}

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="q-name" className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Your Name *</label>
          <input id="q-name" required value={form.name} onChange={set("name")} placeholder="e.g. Rahul Patil" data-testid="quote-name-input" className={`mt-1.5 ${inputCls}`} />
        </div>
        <div>
          <label htmlFor="q-phone" className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Phone Number *</label>
          <input id="q-phone" required value={form.phone} onChange={set("phone")} placeholder="10-digit mobile number" inputMode="tel" data-testid="quote-phone-input" className={`mt-1.5 ${inputCls}`} />
        </div>
        <div>
          <label htmlFor="q-city" className="text-xs font-semibold text-slate-700 uppercase tracking-wider">City / Village</label>
          <input id="q-city" value={form.city} onChange={set("city")} placeholder="e.g. Shahada, Nandurbar" data-testid="quote-city-input" className={`mt-1.5 ${inputCls}`} />
        </div>
        <div>
          <label htmlFor="q-property" className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Property Type</label>
          <select id="q-property" value={form.property_type} onChange={set("property_type")} data-testid="quote-property-select" className={`mt-1.5 ${inputCls}`}>
            {PROPERTY_TYPES.map((t) => <option key={t}>{t}</option>)}
          </select>
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="q-bill" className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Monthly Electricity Bill</label>
          <select id="q-bill" value={form.bill_range} onChange={set("bill_range")} data-testid="quote-bill-select" className={`mt-1.5 ${inputCls}`}>
            {BILL_RANGES.map((t) => <option key={t}>{t}</option>)}
          </select>
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="q-message" className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Message (optional)</label>
          <textarea id="q-message" rows={3} value={form.message} onChange={set("message")} placeholder="Roof size, number of floors, anything else…" data-testid="quote-message-input" className={`mt-1.5 ${inputCls} resize-none`} />
        </div>
      </div>

      <button
        type="submit"
        disabled={loading}
        data-testid="quote-submit-button"
        className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-orange-600 px-8 py-4 text-base font-semibold text-white shadow-lg shadow-orange-600/25 hover:bg-orange-700 disabled:opacity-60 transition-colors"
      >
        {loading ? "Sending…" : "Send Quote Request"} <Send className="h-4 w-4" />
      </button>
    </form>
  );
};

const Contact = () => (
  <div>
    {/* HEADER */}
    <section className="bg-gradient-to-b from-amber-50/80 to-white border-b border-slate-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
        <p className="text-xs sm:text-sm font-semibold tracking-[0.2em] uppercase text-amber-600" data-testid="contact-eyebrow">
          Contact Us
        </p>
        <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-950">
          Let's talk solar.
        </h1>
        <p className="mt-5 text-lg text-slate-600 max-w-2xl leading-relaxed">
          Call us, or leave your details and we will call you — a free site survey
          across {SERVED_AREAS}.
        </p>
        <a
          href={PHONE_TEL}
          data-testid="contact-call-banner"
          className="mt-8 inline-flex flex-wrap items-center gap-3 rounded-2xl bg-slate-950 px-6 py-4 text-white shadow-lg hover:bg-slate-900 transition-colors"
        >
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-orange-600">
            <Phone className="h-5 w-5" />
          </span>
          <span>
            <span className="block text-[11px] font-semibold uppercase tracking-wider text-slate-400">Prefer to talk? Call us directly</span>
            <span className="block font-display text-xl font-bold">{PHONE_DISPLAY}</span>
          </span>
        </a>
      </div>
    </section>

    {/* FORM + INFO */}
    <section className="py-16 lg:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-5 gap-10">
        <div className="lg:col-span-3">
          <Reveal>
            <QuoteForm />
          </Reveal>
        </div>
        <div className="lg:col-span-2 space-y-5">
          <Reveal delay={0.05}>
            <div className="rounded-3xl border border-slate-200 bg-white p-7" data-testid="contact-phone-card">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-amber-100">
                <Phone className="h-5 w-5 text-orange-600" />
              </div>
              <h3 className="mt-4 font-display text-lg font-bold text-slate-900">Phone</h3>
              <a href={PHONE_TEL} data-testid="contact-phone-link" className="mt-1 block text-sm font-semibold text-orange-600 hover:text-orange-700">
                {PHONE_DISPLAY}
              </a>
              <p className="mt-1 text-xs text-slate-500">Mon – Sat, 8 AM – 8 PM</p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="rounded-3xl border border-slate-200 bg-white p-7" data-testid="contact-address-card">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-amber-100">
                <MapPin className="h-5 w-5 text-orange-600" />
              </div>
              <h3 className="mt-4 font-display text-lg font-bold text-slate-900">Office</h3>
              <p className="mt-1 text-sm leading-relaxed text-slate-600">{ADDRESS}</p>
              <a
                href={MAPS_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-map-link"
                className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-orange-600 hover:text-orange-700"
              >
                <Navigation className="h-4 w-4" /> Open in Google Maps
              </a>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="rounded-3xl border border-slate-200 bg-white p-7" data-testid="contact-hours-card">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-amber-100">
                <Clock className="h-5 w-5 text-orange-600" />
              </div>
              <h3 className="mt-4 font-display text-lg font-bold text-slate-900">Working Hours</h3>
              <p className="mt-1 text-sm leading-relaxed text-slate-600">
                {HOURS}
                <br />Sunday: site visits by appointment
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="rounded-3xl bg-slate-950 text-white p-7" data-testid="contact-area-card">
              <h3 className="font-display text-lg font-bold">Service Area</h3>
              <div className="mt-4 flex flex-wrap gap-2">
                {["Nandurbar", "Shahada", "Dhule", "Navapur", "Akkalkuwa", "Taloda"].map((a) => (
                  <span key={a} className="rounded-full bg-slate-800 border border-slate-700 px-3.5 py-1.5 text-xs font-semibold text-amber-400">
                    {a}
                  </span>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  </div>
);

export default Contact;
